import React, { createContext, useContext, useState, useCallback } from 'react';

const STORAGE_KEY = 'homeLocation';

function haversine(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

const HomeLocationContext = createContext(null);

export function HomeLocationProvider({ children }) {
  const [homeLocation, setHomeLocationState] = useState(() => loadFromStorage());

  const setHomeLocation = useCallback((loc) => {
    setHomeLocationState(loc);
    if (loc) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const clearHomeLocation = useCallback(() => {
    setHomeLocation(null);
  }, [setHomeLocation]);

  // Returns distance in km between home and a store, or null if no custom location set
  // or store has no coordinates.
  const computeDistance = useCallback((storeLat, storeLon) => {
    if (!homeLocation || storeLat == null || storeLon == null) return null;
    return haversine(homeLocation.lat, homeLocation.lon, storeLat, storeLon);
  }, [homeLocation]);

  return (
    <HomeLocationContext.Provider value={{ homeLocation, setHomeLocation, clearHomeLocation, computeDistance }}>
      {children}
    </HomeLocationContext.Provider>
  );
}

export function useHomeLocation() {
  return useContext(HomeLocationContext);
}
